<template lang="pug">
	.app--admin-dashboard.flex-col.space-large(v-if="$auth.isAdmin()")
		.font-big.upper.grey {{$tCaption('Administration dashboard')}}
		.flex-col.space
			.flex-row.space
				.flex-col
					.font-small.upper.grey {{$tCaption('Start date')}}
					DxDateBox(:value.sync="filters.startDate")
				.flex-col
					.font-small.upper.grey {{$tCaption('End date')}}
					DxDateBox(:value.sync="filters.endDate")
				.flex-col
					.font-small.upper.grey {{$tCaption('Owner')}}					
					Dropdown.full-width(
						v-model="filters.ownerId",
						:multiple="false",
						:getData="getCompanies", 
						:getItems="r => r.Results",
						:columns="[{ dataField: 'Name', caption: () => $tCaption('Name') }]",
						:showColumnHeaders="false",
						valueExpr="Oid",
						displayExpr="Name")
				.flex-col
					.font-small.upper.grey {{$tCaption('Campaign')}}
					Dropdown(
						ref="campaigns",
						:getTotal="(data) => data.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="getCampaigns",
						:columns="campaignColumns",
						displayExpr="Name",
						valueExpr="Oid",
						:placeholder="$tCaption('Choose campaign')",
						v-model="filters.campaignId",
						:showColumnHeaders="false",
						:filtersVisible="false",
						:searchVisible="true",
						:multiple="false",
						:reloadOnOpen="true"
					)
				.flex-col
					.font-small.upper.grey {{$tCaption('Creatives')}}
					Dropdown(
						ref="creatives",
						:getTotal="(data) => data.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="getCreatives",
						:columns="creativeColumns",
						displayExpr="Name",
						valueExpr="Oid",
						:placeholder="$tCaption('Choose creatives')",
						v-model="filters.creativeIds",
						:showColumnHeaders="true",
						:filtersVisible="true",
						:searchVisible="true",
						:multiple="true",
						:dropDownOptions="dropDownOptions",
						:reloadOnOpen="true"
					)
				.flex-col(:class="{ 'disabled-controls': !filters.creativeIds || !filters.creativeIds.length }")
					.font-small.upper.grey {{$tCaption('Targets')}}
					Dropdown(
						:getTotal="(data) => data.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="getTargets",
						:columns="targetColumns",
						displayExpr="Name",
						valueExpr="Oid",
						:placeholder="$tCaption('Choose targets')",
						v-model="filters.targetIds",
						:showColumnHeaders="false",
						:filtersVisible="true",
						:searchVisible="true",
						:multiple="true",
						:dropDownOptions="dropDownOptions",
						:reloadOnOpen="true"
					)
			.flex-col.space(v-if="dashboardData.length")
				.flex-row.wrap
					.info-block.flex-col.space.align-center.center(v-for="total in totals")
						.font-small.grey.upper {{total.caption}}
						.font-big {{$Data.formatNumber(total.value)}}
				DxDataGrid.flex-1(:height="600", :columns="gridColumns", :data-source="dashboardData", :paging="{ pageSize: 10 }", :summary="summary", :filterRow="{ visible: true }", :columnAutoWidth="true")
		Loader(v-if="isLoading", :text="$tCaption('Loading', '@actions')", :fullscreen="true")
</template>

<script>
	import _ from 'lodash';
	import DxDateBox from 'devextreme-vue/date-box';
	import Dropdown from 'app-core/Dropdown.vue';
	import DxDataGrid from 'devextreme-vue/data-grid';
	import Loader from 'app-core/Loader.vue'

	export default {
		translationCategory: 'dashboard.AdminDashboard',
		name: 'admin-dashboard',
		components: {
			DxDateBox, Dropdown, DxDataGrid, Loader
		},
		data() {
			return {
				filters: {
					startDate: this.$moment().startOf('day').toDate(),
					endDate: this.$moment().startOf('day').toDate(),
					ownerId: null,
					campaignId: null,
					creativeIds: [],
					targetIds: []
				},
				campaignColumns: [
					{ dataField: 'Name', caption: this.$tCaption('Campaign') }
				],
				creativeColumns: [
					{ dataField: 'Name', caption: this.$tCaption('Creative'), width: 200 }, 
					{ dataField: 'LastOnAir', caption: this.$tCaption('Last on air'), dataType: 'date' }, 
					{ dataField: 'Folder.Name', caption: this.$tCaption('Folder') }
				],
				targetColumns: [
					{ dataField: 'Name', caption: this.$tCaption('Target') }
				],
				dropDownOptions: {
					minWidth: '500px'
				},
				gridColumns: [
					{ dataField: 'Date', caption: this.$tCaption('Date'), dataType: 'date' },
					{ dataField: 'CreativeName', caption: this.$tCaption('Creative'), dataType: 'string', width: 200 },
					{ dataField: 'SEventImpression', caption: this.$tCaption('Impression'), dataType: 'number', sum: true, format: 'fixedPoint', showTotals: true },
					{ dataField: 'SEventFirstQuartile', caption: this.$tCaption('First quartile'), dataType: 'number', sum: true, format: 'fixedPoint', showTotals: true },
					{ dataField: 'SEventMidpoint', caption: this.$tCaption('Midpoint'), dataType: 'number', sum: true, format: 'fixedPoint', showTotals: true },
					{ dataField: 'SEventThirdQuartile', caption: this.$tCaption('Third quartile'), dataType: 'number', sum: true, format: 'fixedPoint', showTotals: true },
					{ dataField: 'SEventComplete', caption: this.$tCaption('Complete'), dataType: 'number', sum: true, format: 'fixedPoint', showTotals: true },
					{ dataField: 'SEventSkip', caption: this.$tCaption('Skip'), dataType: 'number', sum: true, format: 'fixedPoint', showTotals: true },
					{ dataField: 'SEventClickTracking', caption: this.$tCaption('Click tracking'), dataType: 'number', sum: true, format: 'fixedPoint', showTotals: true },
					{ dataField: 'CountOfVastRequests', caption: this.$tCaption('VAST requests'), dataType: 'number', sum: true, format: 'fixedPoint', showTotals: true },
				],
				dashboardData: [],
				isLoading: false,
			};
		},
		computed: {
			totals() {
				const totals = {};
				const cols = this.gridColumns.filter(f => f.showTotals === true);

				this.dashboardData.map(f => {
					cols.map(c => {
						totals[c.dataField] = (totals[c.dataField] || 0) + f[c.dataField];
					});
				});

				return Object.keys(totals).map(f => ({
					id: f,
					caption: cols.find(c => c.dataField === f).caption,
					value: totals[f]
				}));
			},
			summary() {
				return {
					totalItems: this.gridColumns.filter(f => f.sum === true).map(f => ({
						column: f.dataField,
						summaryType: 'sum',
						valueFormat: 'fixedPoint',
						displayFormat: '{0}'
					}))
				}
			}
		},
		watch: {
			filters: {
				handler() {
					this.$loadData();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.adminDashboard = this;
			this.$loadData = _.debounce(this.loadData, 1000, {
				leading: false,
				trailing: true,
				maxWait: 3000
			});
		},
		destroyed() {
			delete this.$loadData;
		},
		methods: {
			getCompanies(pageIndex, pageSize, opts) {
				if (__DEBUG__)
					console.debug('Get companies', { pageIndex, pageSize, opts });
				const filter = [];
				if (opts?.filter) {
					filter.push(opts?.filter);
				}
				const filterString = filter?.length ? `&filterCriteria=${encodeURIComponent(JSON.stringify(filter))}` : '';
				return this.$Data.getExternalApi('Company/GetCompaniesWithoutSecurity', `page=${pageIndex + 1}${filterString}`, pageSize);
			},
			getCampaigns(pageIndex, pageSize, opts) {
				if (__DEBUG__)
					console.debug('Get campaigns', { pageIndex, pageSize, opts });
				const filter = [];
				if (opts?.filter) {
					filter.push(opts?.filter);
				}
				if (this.filters.ownerId) {
					if (filter.length) filter.push('and');
					filter.push(['Owner.Oid', '=', this.filters.ownerId]);
				}
				const filterString = filter?.length ? `&filterCriteria=${encodeURIComponent(JSON.stringify(filter))}` : '';
				return this.$Data.getExternalApi('Campaign/GetCampaignsWithoutSecurity', `page=${pageIndex + 1}${filterString}`, pageSize);
			},
			getCreatives(pageIndex, pageSize, opts) {
				if (__DEBUG__)
					console.debug('Get creatives', { pageIndex, pageSize, opts });
				const filter = [];
				if (opts?.filter) {
					filter.push(opts?.filter);
				}
				if (this.filters.ownerId) {
					if (filter.length) filter.push('and');
					filter.push(['Owner.Oid', '=', this.filters.ownerId]);
				}
				if (this.filters.campaignId) {
					if (filter.length) filter.push('and');
					filter.push(['Campaigns.Oid', 'contains', [this.filters.campaignId]]);
				}
				const filterString = filter?.length ? `&filterCriteria=${encodeURIComponent(JSON.stringify(filter))}` : '';
				return this.$Data.getExternalApi('Creative/GetAllCreativesWithoutSecurity', `sorting=[{ selector: 'LastOnAir', desc: true }]&page=${pageIndex + 1}${filterString}`, pageSize);
			},
			async getTargets(pageIndex, pageSize, opts) {
				if (__DEBUG__)
					console.debug('Get targets', { pageIndex, pageSize, opts });
				if (!this.filters.creativeIds?.length) return { Results: [] };

				const filterString = opts?.filter ? `&filterCriteria=${encodeURIComponent(JSON.stringify(opts?.filter))}` : '';
				return this.$Data.getExternalApi('Creative/GetTargetsByCreatives', `creativeIds=${this.filters.creativeIds.join(',')}&page=${pageIndex + 1}${filterString}`, pageSize);
			},
			loadData() {
				if (__DEBUG__)
					console.debug('Get dashboard data', { });
				const filter = [];
				if (this.filters.startDate) {					
					filter.push(`&dateStart=${this.$moment(this.filters.startDate).format('YYYY-MM-DD')}`);
				}
				if (this.filters.endDate) {
					filter.push(`&dateEnd=${this.$moment(this.filters.endDate).format('YYYY-MM-DD')}`);
				}
				let haveSomeValueableFilter = false;
				if (this.filters.campaignId) {
					filter.push(`&campaignId=${this.filters.campaignId}`);
					haveSomeValueableFilter = true;
				}
				if (this.filters.creativeIds?.length) {
					filter.push(`&creativeIds=${this.filters.creativeIds.join(',')}`);
					haveSomeValueableFilter = true;
				}
				if (this.filters.targetIds?.length) {
					filter.push(`&targetIds=${this.filters.targetIds.join(',')}`);
					haveSomeValueableFilter = true;
				}

				if (!haveSomeValueableFilter) {
					this.dashboardData = [];
					return;
				}
				
				this.isLoading++;
				return this.$Data.getExternalApi('DashboardData/GetAdminDashboardData', `page=${1}${filter.join('')}`, 99999).then(r => {
					this.dashboardData = r;
				}).finally(() => this.isLoading--);
			}
		}
	};
</script>
<style lang='scss'>
	.app--admin-dashboard {
		@import "main/themes/generated/variables.base.scss";

		padding: 20px;

		.info-block {
			padding: 20px;
			background: #fefefe;
			box-shadow: 0px 4px 10px #ddd;
			margin: 4px;
		}
	}
</style>
